/**
 * @file Sends one PDF straight to PDF intake: uploads it to the caller's temp
 * folder, picks the order form the schedule is written to, fires the
 * `startPdfIntakeSchedule` task without awaiting it, and returns the job id
 * (the SHA-256 of the bytes) so the caller can open S2. Consumers: `useFileDrop`,
 * `useFormImportDrop`, `useExtractSchedule` and the Intake Upload dialog; the
 * caller navigates, so the service also serves screens that do not. On
 * `{ newForm }` a job already stored for the same bytes keeps its order and no
 * draft form is created, so a second drop leaves no orphan form. A `source` is
 * sent only when given, and the server stores it on a new job only.
 * The service keeps the file's `intakeState.uploads` entry: `uploading` from
 * the hash on, `sent` once the task is sent, deleted when the task confirms;
 * when the task fails, deleted if the job document exists, else it carries the
 * error, so S1's Uploading row and S2 show it. Each write assigns a whole entry
 * built from locals. Only a refusal is toasted; S2 watches the job document.
 */
import { doc, getDoc } from 'firebase/firestore';
import type { MessageInstance } from 'antd/es/message/interface';
import { pdfIntakeJobPath } from '#types';
import type {
  FormTypeId,
  PdfIntakeJob,
  PdfIntakeJobSource,
  PdfIntakeOrderRef,
} from '#types';
import { fileSha256, uploadTempAttachment } from '@/storage';
import { callPdfIntakeTask } from '@/components/agents/review/callPdfIntakeTask';
import { intakeState } from '@/components/agents/review/intakeState';
import { isTaskRefusal } from '@/components/agents/review/isTaskRefusal';
import { createImportForm } from './createImportForm';
import { firestore } from './startFirestore';

const NOT_SENT = 'The file could not be sent to intake.';

async function orderFor(
  order: PdfIntakeOrderRef | { newForm: FormTypeId },
  fileSha256: string,
): Promise<PdfIntakeOrderRef> {
  if (!('newForm' in order)) return order;
  const snapshot = await getDoc(doc(firestore, pdfIntakeJobPath(fileSha256)));
  const job = snapshot.data() as PdfIntakeJob | undefined;
  if (job?.kind === 'schedule' && job.order) return job.order;
  return createImportForm(order.newForm, `New ${order.newForm}`);
}

export async function startPdfIntake(params: {
  file: File;
  /** The order form to write to, or `{ newForm }`: a draft form of that type is created unless a job for the same bytes already names its order. */
  order: PdfIntakeOrderRef | { newForm: FormTypeId };
  userId: string;
  messageApi: MessageInstance;
  /** The attachment version the bytes were read from; stored on a new job only. */
  source?: PdfIntakeJobSource;
}): Promise<string | null> {
  const { file, userId, messageApi, source } = params;
  const fileName = file.name;
  try {
    const content = new Uint8Array(await file.arrayBuffer());
    const jobId = await fileSha256(content);
    intakeState.uploads[jobId] = { fileName, kind: 'schedule', progress: 'uploading' };
    try {
      const { fileFolder } = await uploadTempAttachment({
        content,
        filename: fileName,
        contentType: file.type || 'application/pdf',
        userId,
      });
      const order = await orderFor(params.order, jobId);
      intakeState.uploads[jobId] = { fileName, kind: 'schedule', progress: 'sent' };
      void callPdfIntakeTask({
        taskType: 'startPdfIntakeSchedule',
        fileFolder,
        filename: fileName,
        order,
        sourceTypeId: 'pdf',
        ...(source ? { source } : {}),
      }).then(
        () => {
          delete intakeState.uploads[jobId];
        },
        async (error: unknown) => {
          const message = error instanceof Error ? error.message : NOT_SENT;
          if (isTaskRefusal(error)) void messageApi?.error?.(message);
          const exists = await getDoc(doc(firestore, pdfIntakeJobPath(jobId))).then(
            (snapshot) => snapshot.exists(),
            () => false,
          );
          if (exists) delete intakeState.uploads[jobId];
          else intakeState.uploads[jobId] = { fileName, kind: 'schedule', progress: 'sent', error: message };
        },
      );
      return jobId;
    } catch (error) {
      intakeState.uploads[jobId] = {
        fileName,
        kind: 'schedule',
        progress: 'uploading',
        error: error instanceof Error ? error.message : NOT_SENT,
      };
      throw error;
    }
  } catch (error) {
    console.error('startPdfIntake failed', error);
    void messageApi?.error?.(error instanceof Error ? error.message : NOT_SENT);
    return null;
  }
}
